/* SPOTEC — change colour on the product page without a reload (rev. 9 Oct 2026 B).
   1. Tap a colour: the chip, colour name, title and main photo change straight away (no waiting).
   2. The full product (sizes, stock, price, gallery, Product Story) is swapped in from HTML that was
      prefetched quietly after the page loaded, so it is usually already there.
   3. The scroll position never moves: smooth scrolling and scroll anchoring are switched off during the swap.
   Data Saver / 2G: nothing is prefetched; the page is fetched at the first touch instead. */
(() => {
  if (window.spotecProductNavigation) return;
  window.spotecProductNavigation = 2;

  const TTL = 5 * 60 * 1000;      // prefetched pages stay fresh for 5 minutes
  const MAX_CACHE = 12;
  const MAX_PREFETCH = 4;        // nearest colours prefetched on 4G/Wi-Fi (2 on 3G); others load at the first touch
  const IMAGE_WAIT = 350;         // wait at most this long for the new main photo before swapping
  const root = document.documentElement;
  const cache = new Map();
  const warmed = new Set();
  let request = 0;
  let transition = null;

  const conn = navigator.connection || {};
  // Data Saver / 2G: nothing is prefetched. 3G (common on Indonesian mobile networks): only the 2 nearest colours.
  const lowData = () => !!conn.saveData || /(^|-)2g/.test(conn.effectiveType || '');
  const slowData = () => conn.effectiveType === '3g';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const chipSelector = '.sf-color-family [data-sf-color-choice]';

  const urlFor = (value) => {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product URL');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const keyFor = (value) => {
    const url = urlFor(value);
    url.searchParams.delete('preview_theme_id');
    return url.pathname + url.search;
  };

  const load = (url, priority = 'high') => {
    const saved = cache.get(url.href);
    if (saved && Date.now() - saved.time < TTL) return saved.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    const init = { credentials: 'same-origin', signal: controller.signal, priority };
    const promise = fetch(url.href, init)
      .then((response) => {
        if (!response.ok || new URL(response.url || url.href).origin !== location.origin) throw new Error('Product unavailable');
        return response.text();
      })
      .finally(() => clearTimeout(timeout));
    cache.set(url.href, { promise, time: Date.now() });
    promise.catch(() => cache.delete(url.href));
    while (cache.size > MAX_CACHE) cache.delete(cache.keys().next().value);
    return promise;
  };

  const warmImage = (chip) => {
    const src = chip && chip.dataset.colorImage;
    if (!src || warmed.has(src)) return;
    warmed.add(src);
    const image = new Image();
    image.decoding = 'async';
    image.src = src;
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const chipPhotoReady = (chip) => {
    const image = new Image();
    image.src = chip.dataset.colorImage;
    return image.decode().catch(() => {});
  };
  const readyImage = (main) => {
    const source = activePhoto(main);
    if (!source) return Promise.resolve();
    source.loading = 'eager';
    const image = new Image();
    image.sizes = source.getAttribute('sizes') || '100vw';
    image.srcset = source.getAttribute('srcset') || '';
    image.src = source.getAttribute('src');
    return Promise.race([image.decode().catch(() => {}), sleep(IMAGE_WAIT)]);
  };

  const activePhoto = (main) => main.querySelector('media-gallery .is-active .product__media img') || main.querySelector('media-gallery .product__media img');
  const samePath = (a, b) => {
    try { return new URL(a, location.href).pathname === new URL(b, location.href).pathname; } catch (error) { return false; }
  };
  // When the new page's first photo is the same file as the chip photo (already loaded at the tap),
  // show that copy straight away instead of waiting for another size; the sharper sizes come back right after.
  const borrowChipPhoto = (main, chip) => {
    const src = chip && chip.dataset.colorImage;
    const photo = activePhoto(main);
    if (!src || !photo || !samePath(src, photo.getAttribute('src'))) return null;
    const srcset = photo.getAttribute('srcset') || '';
    photo.removeAttribute('srcset');
    photo.loading = 'eager';
    photo.src = src;
    return srcset;
  };

  // Instant feedback: everything we can show from the chip itself, before the network answers.
  const showChoice = (main, chip) => {
    const family = chip.closest('.sf-color-family');
    if (family) {
      family.querySelectorAll('[data-sf-color-choice][aria-current]').forEach((other) => other.removeAttribute('aria-current'));
      const label = family.querySelector('[data-sf-color-label]');
      if (label && chip.dataset.colorLabel) label.textContent = chip.dataset.colorLabel;
    }
    chip.setAttribute('aria-current', 'true');
    const title = main.querySelector('.product__title h1');
    if (title && chip.dataset.colorTitle) title.textContent = chip.dataset.colorTitle;
    // The browser keeps showing the old photo until the new one has loaded, so there is no blank frame.
    const photo = activePhoto(main);
    if (photo && chip.dataset.colorImage) {
      photo.removeAttribute('srcset');
      photo.src = chip.dataset.colorImage;
    }
  };

  const setBusy = (main, busy) => {
    const buyArea = main && main.querySelector('[data-sf-marketplace]');
    root.classList.toggle('sf-product-pending', busy);
    if (!main) return;
    if (busy) main.setAttribute('aria-busy', 'true'); else main.removeAttribute('aria-busy');
    if (buyArea) buyArea.inert = busy;
  };

  // Keep the page exactly where it is while the product content is replaced.
  const holdScroll = (x, y) => {
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    root.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';
    const put = () => window.scrollTo({ left: x, top: y, behavior: 'instant' });
    put();
    return () => {
      put();
      requestAnimationFrame(() => {
        put();
        root.style.scrollBehavior = previous;
        root.style.overflowAnchor = '';
        document.body.style.overflowAnchor = '';
      });
    };
  };

  async function navigate(url, chip, push = true) {
    const token = ++request;
    if (transition && transition.skipTransition) transition.skipTransition();
    const oldMain = document.querySelector('main');
    if (!oldMain || typeof HTMLUpdateUtility === 'undefined') { location.assign(url.href); return; }
    if (chip) showChoice(oldMain, chip);
    setBusy(oldMain, true);
    try {
      const html = new DOMParser().parseFromString(await load(url), 'text/html');
      if (token !== request) return;
      const nextMain = html.querySelector('main');
      if (!nextMain || !nextMain.querySelector('product-info')) throw new Error('Missing product');
      const borrowed = borrowChipPhoto(nextMain, chip);
      await (borrowed === null ? readyImage(nextMain) : Promise.race([chipPhotoReady(chip), sleep(IMAGE_WAIT)]));
      if (token !== request) return;

      // Keep the colour rail in the same order as before (each product page lists itself first).
      const rail = nextMain.querySelector('.sf-color-family [data-sf-color-track]');
      const order = Array.from(oldMain.querySelectorAll(chipSelector), (item) => keyFor(item.href));
      if (rail) {
        const choices = new Map(Array.from(rail.children, (item) => [keyFor(item.href), item]));
        order.forEach((key) => { if (choices.has(key)) rail.append(choices.get(key)); });
      }
      nextMain.dataset.sfProductSwapped = '';
      nextMain.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
      const scroll = { x: scrollX, y: scrollY };

      const commit = () => {
        if (token !== request) return;
        const release = holdScroll(scroll.x, scroll.y);
        if (window.pauseAllMedia) window.pauseAllMedia();
        const sectionIds = new Set(Array.from(oldMain.querySelectorAll('.shopify-section[id]'), (node) => node.id.replace('shopify-section-', '')));
        document.querySelectorAll('body > modal-dialog, body > product-modal').forEach((modal) => {
          if (sectionIds.has(modal.dataset.section)) modal.remove();
        });
        document.body.classList.remove('overflow-hidden');
        const oldInfo = oldMain.querySelector('product-info');
        if (oldInfo && oldInfo.abortController) oldInfo.abortController.abort();
        HTMLUpdateUtility.viewTransition(oldMain, nextMain, [], [(newMain) => {
          if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          newMain.querySelectorAll('.shopify-section').forEach((section) => section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true })));
          const selected = newMain.querySelector('.sf-color-family [aria-current="true"]');
          if (selected) selected.focus({ preventScroll: true });
          if (borrowed) {
            const photo = activePhoto(newMain);
            if (photo) setTimeout(() => { if (photo.isConnected) photo.setAttribute('srcset', borrowed); }, 400);
          }
        }]);
        oldMain.remove();   // do not keep the hidden copy around: it changes the page height for half a second
        document.title = html.title;
        const canonical = document.querySelector('link[rel="canonical"]');
        const nextCanonical = html.querySelector('link[rel="canonical"]');
        if (canonical && nextCanonical) canonical.href = nextCanonical.href;
        if (push) history.pushState({ ...history.state, spotecColorNavigation: true }, '', url.href);
        release();
      };

      const animate = document.startViewTransition && document.visibilityState === 'visible' && !reduceMotion();
      if (animate) {
        root.classList.add('sf-product-changing');
        transition = document.startViewTransition(commit);
        await transition.finished.catch(() => {});
      } else {
        commit();
      }
      prefetchRail();
    } catch (error) {
      if (token === request) location.assign(url.href);
    } finally {
      if (token === request) {
        root.classList.remove('sf-product-changing');
        setBusy(document.querySelector('main'), false);
      }
    }
  }

  // Quietly fetch the other colours once the page is idle, so a tap is answered from memory.
  let prefetched = false;
  function prefetchRail() {
    if (lowData()) return;
    const all = Array.from(document.querySelectorAll(chipSelector));
    const at = Math.max(0, all.findIndex((chip) => chip.getAttribute('aria-current') === 'true'));
    const chips = all
      .map((chip, i) => ({ chip, distance: Math.abs(i - at) }))
      .filter((item) => item.distance > 0)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, slowData() ? 2 : MAX_PREFETCH)
      .map((item) => item.chip);
    let index = 0;
    const next = () => {
      const chip = chips[index++];
      if (!chip) return;
      let url;
      try { url = urlFor(chip.href); } catch (error) { next(); return; }
      load(url, 'low').catch(() => {}).finally(next);
    };
    next(); next();   // two at a time
  }
  function schedulePrefetch() {
    if (prefetched || !document.querySelector(chipSelector)) return;
    prefetched = true;
    const run = () => ('requestIdleCallback' in window ? requestIdleCallback(prefetchRail, { timeout: 4000 }) : setTimeout(prefetchRail, 1500));
    if (document.readyState === 'complete') setTimeout(run, 800);
    else addEventListener('load', () => setTimeout(run, 800), { once: true });
  }

  document.addEventListener('click', (event) => {
    const chip = event.target.closest(chipSelector);
    if (!chip || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || chip.target === '_blank') return;
    let url;
    try { url = urlFor(chip.href); } catch (error) { return; }
    event.preventDefault();
    // Stop the older full-navigation rail handler from changing the order again.
    event.stopPropagation();
    if (keyFor(url.href) === keyFor(location.href)) {
      ++request;
      if (transition && transition.skipTransition) transition.skipTransition();
      root.classList.remove('sf-product-changing');
      const main = document.querySelector('main');
      setBusy(main, false);
      if (main) showChoice(main, chip);
      return;
    }
    navigate(url, chip);
  }, true);

  // Start loading at the first touch / hover, a little before the click arrives.
  const early = (event) => {
    const chip = event.target.closest && event.target.closest(chipSelector);
    if (!chip) return;
    warmImage(chip);
    try { load(urlFor(chip.href)).catch(() => {}); } catch (error) {}
  };
  document.addEventListener('pointerdown', early, { passive: true });
  document.addEventListener('pointerover', (event) => { if (event.pointerType === 'mouse' && !lowData()) early(event); }, { passive: true });
  document.addEventListener('focusin', early);

  if (document.querySelector('product-info')) {
    history.replaceState({ ...history.state, spotecColorNavigation: true }, '', location.href);
    schedulePrefetch();
  }
  window.addEventListener('popstate', (event) => {
    if (!event.state || !event.state.spotecColorNavigation || !document.querySelector('product-info')) return;
    try {
      const url = urlFor(location.href);
      const key = keyFor(url.href);
      const chip = Array.from(document.querySelectorAll(chipSelector)).find((item) => keyFor(item.href) === key);
      navigate(url, chip || null, false);
    } catch (error) {
      location.reload();
    }
  });
})();
